import React, { useEffect, useState } from 'react';

import slide1 from '../images/slide1.jpg';
import slide2 from '../images/slide2.jpg';
import slide3 from '../images/slide3.jpg';
import slide4 from '../images/slide4.jpg';

const Landingscreen = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState('next');

  const colors = {
    white: '#ffffff',
    heading: '#0f172a',
    body: '#475569',
    blue: '#0284c7',
    blueDark: '#0369a1',
    blueLight: '#e0f2fe',
    sky: '#38bdf8',
    border: 'rgba(255,255,255,0.25)',
  };

  const slides = [
    {
      image: slide1,
      eyebrow: 'STRUCTURAL ENGINEERING',
      title: 'Engineering Precision',
      highlight: 'Built for Reality.',
      description:
        'Advanced structural analysis and design solutions engineered for safety, efficiency, constructability, and long-term performance.',
      primary: 'Explore Services',
      secondary: 'Our Projects',
    },
    {
      image: slide2,
      eyebrow: 'INDUSTRIAL & COMMERCIAL',
      title: 'Structures That',
      highlight: 'Perform Under Pressure.',
      description:
        'From industrial facilities to commercial buildings, we develop optimized structural systems that balance performance, economy, and execution.',
      primary: 'View Capabilities',
      secondary: 'Contact Us',
    },
    {
      image: slide3,
      eyebrow: 'DIGITAL ENGINEERING',
      title: 'From Engineering Data',
      highlight: 'To Digital Reality.',
      description:
        'STAAD.Pro, IDEA StatiCa, RAM Connection, and Tekla workflows bring analysis, detailing, coordination, and fabrication together.',
      primary: 'Explore Technology',
      secondary: 'View Services',
    },
    {
      image: slide4,
      eyebrow: 'LAND DEVELOPMENT',
      title: 'Turning Survey Data',
      highlight: 'Into Buildable Plans.',
      description:
        'Precise Civil 3D drafting, topographic mapping, site development, grading, utilities, and infrastructure documentation.',
      primary: 'View Civil Services',
      secondary: 'Start a Project',
    },
  ];

  const goToSlide = (index, dir = 'next') => {
    setDirection(dir);

    if (index < 0) {
      setCurrentSlide(slides.length - 1);
    } else if (index >= slides.length) {
      setCurrentSlide(0);
    } else {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => {
    goToSlide(currentSlide + 1, 'next');
  };

  const previousSlide = () => {
    goToSlide(currentSlide - 1, 'prev');
  };

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setDirection('next');

      setCurrentSlide((prev) => {
        if (prev === slides.length - 1) {
          return 0;
        }

        return prev + 1;
      });
    }, 6000);

    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        nextSlide();
      }

      if (event.key === 'ArrowLeft') {
        previousSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  const activeSlide = slides[currentSlide];

  const styles = {
    section: {
      position: 'relative',
      width: '100%',
      height: 'calc(100vh - 94px)',
      minHeight: '620px',
      maxHeight: '900px',
      overflow: 'hidden',
      backgroundColor: '#e0f2fe',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    },

    imageWrapper: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },

    image: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      transition:
        direction === 'next'
          ? 'opacity 0.9s ease, transform 7s ease'
          : 'opacity 0.9s ease, transform 7s ease',
      transform: 'scale(1.04)',
    },

    overlay: {
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(90deg, rgba(2, 15, 30, 0.90) 0%, rgba(2, 15, 30, 0.72) 32%, rgba(2, 15, 30, 0.34) 62%, rgba(2, 15, 30, 0.10) 100%)',
      zIndex: 2,
    },

    bottomOverlay: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '180px',
      background:
        'linear-gradient(to top, rgba(2, 15, 30, 0.72), transparent)',
      zIndex: 2,
      pointerEvents: 'none',
    },

    contentContainer: {
      position: 'relative',
      zIndex: 5,
      width: '100%',
      maxWidth: '1400px',
      height: '100%',
      margin: '0 auto',
      padding: '0 6%',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },

    content: {
      width: '700px',
      maxWidth: '100%',
      color: colors.white,
      marginTop: '-20px',
      animation: 'heroContentIn 0.8s ease both',
    },

    eyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 14px',
      borderRadius: '999px',
      backgroundColor: 'rgba(255,255,255,0.10)',
      border: '1px solid rgba(255,255,255,0.25)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      color: '#bae6fd',
      fontSize: '0.78rem',
      fontWeight: '700',
      letterSpacing: '0.14em',
      marginBottom: '1.4rem',
    },

    eyebrowDot: {
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      backgroundColor: colors.sky,
      boxShadow: `0 0 14px ${colors.sky}`,
      animation: 'pulseDot 1.8s ease-in-out infinite',
    },

    heading: {
      fontSize: 'clamp(3.2rem, 6vw, 6.3rem)',
      lineHeight: '0.98',
      fontWeight: '800',
      letterSpacing: '-0.055em',
      margin: 0,
      color: '#ffffff',
    },

    highlight: {
      display: 'block',
      color: '#7dd3fc',
      marginTop: '10px',
    },

    description: {
      maxWidth: '650px',
      fontSize: '1.08rem',
      lineHeight: '1.7',
      color: 'rgba(255,255,255,0.82)',
      margin: '1.7rem 0 0 0',
    },

    buttonRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      flexWrap: 'wrap',
      marginTop: '2rem',
    },

    primaryButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '14px 22px',
      borderRadius: '10px',
      border: '1px solid rgba(255,255,255,0.15)',
      backgroundColor: colors.blue,
      color: '#ffffff',
      fontSize: '0.92rem',
      fontWeight: '700',
      cursor: 'pointer',
      textDecoration: 'none',
      boxShadow: '0 12px 30px rgba(2,132,199,0.28)',
      transition: 'all 0.3s ease',
    },

    secondaryButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '14px 22px',
      borderRadius: '10px',
      border: '1px solid rgba(255,255,255,0.30)',
      backgroundColor: 'rgba(255,255,255,0.08)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      color: '#ffffff',
      fontSize: '0.92rem',
      fontWeight: '600',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
    },

    sliderControls: {
      position: 'absolute',
      zIndex: 8,
      right: '6%',
      bottom: '45px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },

    arrowButton: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      border: '1px solid rgba(255,255,255,0.30)',
      backgroundColor: 'rgba(15,23,42,0.35)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      color: '#ffffff',
      fontSize: '1.2rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
    },

    indicators: {
      position: 'absolute',
      zIndex: 8,
      left: '6%',
      bottom: '48px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },

    indicator: (active) => ({
      width: active ? '42px' : '8px',
      height: '8px',
      borderRadius: '999px',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      backgroundColor: active
        ? colors.sky
        : 'rgba(255,255,255,0.45)',
      boxShadow: active ? `0 0 12px rgba(56,189,248,0.65)` : 'none',
      transition: 'all 0.35s ease',
    }),

    slideCounter: {
      position: 'absolute',
      zIndex: 8,
      right: '6%',
      top: '38px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: '#ffffff',
      fontSize: '0.78rem',
      fontWeight: '600',
      letterSpacing: '0.08em',
    },

    counterLine: {
      width: '50px',
      height: '1px',
      backgroundColor: 'rgba(255,255,255,0.5)',
    },

    pauseButton: {
      position: 'absolute',
      zIndex: 8,
      right: '6%',
      top: '95px',
      width: '38px',
      height: '38px',
      borderRadius: '50%',
      border: '1px solid rgba(255,255,255,0.25)',
      backgroundColor: 'rgba(15,23,42,0.30)',
      color: '#ffffff',
      cursor: 'pointer',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      fontSize: '0.8rem',
    },

    sideLabel: {
      position: 'absolute',
      zIndex: 7,
      right: '6%',
      top: '50%',
      transform: 'translateY(-50%) rotate(90deg)',
      transformOrigin: 'center',
      color: 'rgba(255,255,255,0.65)',
      fontSize: '0.68rem',
      letterSpacing: '0.28em',
      fontWeight: '700',
      textTransform: 'uppercase',
    },

    scrollIndicator: {
      position: 'absolute',
      zIndex: 8,
      left: '50%',
      bottom: '28px',
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      color: 'rgba(255,255,255,0.65)',
      fontSize: '0.62rem',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
    },

    scrollLine: {
      width: '1px',
      height: '34px',
      backgroundColor: 'rgba(255,255,255,0.5)',
      animation: 'scrollLine 2s ease-in-out infinite',
    },
  };

  return (
    <section
      style={styles.section}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <style>{`
        @keyframes heroContentIn {
          0% {
            opacity: 0;
            transform: translateY(35px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulseDot {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.65;
          }
        }

        @keyframes scrollLine {
          0% {
            transform: scaleY(0.4);
            transform-origin: top;
            opacity: 0.3;
          }
          50% {
            transform: scaleY(1);
            transform-origin: top;
            opacity: 1;
          }
          100% {
            transform: scaleY(0.4);
            transform-origin: bottom;
            opacity: 0.3;
          }
        }

        .hero-primary-btn:hover {
          background: #0369a1 !important;
          transform: translateY(-3px);
          box-shadow: 0 18px 35px rgba(2,132,199,0.38) !important;
        }

        .hero-secondary-btn:hover {
          background: rgba(255,255,255,0.18) !important;
          border-color: rgba(255,255,255,0.55) !important;
          transform: translateY(-3px);
        }

        .hero-arrow:hover {
          background: #0284c7 !important;
          border-color: #0284c7 !important;
          transform: scale(1.08);
        }

        .hero-image {
          animation: heroImageZoom 7s ease-out forwards;
        }

        @keyframes heroImageZoom {
          from {
            transform: scale(1.04);
          }
          to {
            transform: scale(1);
          }
        }

        @media (max-width: 1100px) {
          .hero-heading {
            font-size: 4.8rem !important;
          }

          .hero-content {
            width: 620px !important;
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            height: calc(100svh - 75px) !important;
            min-height: 650px !important;
          }

          .hero-content-container {
            padding: 0 6% !important;
            align-items: flex-end !important;
            padding-bottom: 110px !important;
          }

          .hero-content {
            width: 100% !important;
            margin-top: 0 !important;
          }

          .hero-heading {
            font-size: clamp(2.7rem, 11vw, 4rem) !important;
            line-height: 1.02 !important;
          }

          .hero-description {
            font-size: 0.94rem !important;
            line-height: 1.6 !important;
            margin-top: 1.25rem !important;
          }

          .hero-eyebrow {
            font-size: 0.68rem !important;
            padding: 7px 11px !important;
            margin-bottom: 1rem !important;
          }

          .hero-buttons {
            margin-top: 1.4rem !important;
            gap: 9px !important;
          }

          .hero-primary-btn,
          .hero-secondary-btn {
            padding: 12px 16px !important;
            font-size: 0.82rem !important;
          }

          .hero-indicators {
            left: 6% !important;
            bottom: 35px !important;
          }

          .hero-controls {
            right: 6% !important;
            bottom: 28px !important;
          }

          .hero-arrow {
            width: 40px !important;
            height: 40px !important;
            font-size: 1rem !important;
          }

          .hero-counter {
            right: 6% !important;
            top: 25px !important;
          }

          .hero-pause {
            right: 6% !important;
            top: 72px !important;
          }

          .hero-side-label {
            display: none !important;
          }

          .hero-scroll {
            display: none !important;
          }

          .hero-overlay {
            background:
              linear-gradient(
                90deg,
                rgba(2,15,30,0.92) 0%,
                rgba(2,15,30,0.70) 65%,
                rgba(2,15,30,0.35) 100%
              ) !important;
          }
        }

        @media (max-width: 480px) {
          .hero-section {
            min-height: 620px !important;
          }

          .hero-content-container {
            padding-bottom: 105px !important;
          }

          .hero-heading {
            font-size: 2.65rem !important;
          }

          .hero-description {
            max-width: 100% !important;
            font-size: 0.88rem !important;
          }

          .hero-secondary-btn {
            display: none !important;
          }

          .hero-indicators {
            bottom: 30px !important;
          }

          .hero-controls {
            bottom: 23px !important;
          }

          .hero-counter {
            top: 20px !important;
          }

          .hero-pause {
            top: 65px !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-image,
          .hero-content,
          .hero-scroll-line,
          .hero-eyebrow-dot {
            animation: none !important;
          }
        }
      `}</style>

      {/* ================= IMAGE ================= */}

      <div style={styles.imageWrapper}>
        <img
          key={`${currentSlide}-${direction}`}
          src={activeSlide.image}
          alt={activeSlide.title}
          style={styles.image}
          className="hero-image"
        />

        <div
          style={styles.overlay}
          className="hero-overlay"
        />

        <div style={styles.bottomOverlay} />
      </div>

      {/* ================= SLIDE COUNTER ================= */}

      <div
        style={styles.slideCounter}
        className="hero-counter"
      >
        <span>
          {String(currentSlide + 1).padStart(2, '0')}
        </span>

        <span style={styles.counterLine} />

        <span>
          {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* ================= PAUSE ================= */}

      <button
        type="button"
        onClick={() => setIsPaused(!isPaused)}
        style={styles.pauseButton}
        className="hero-pause"
        aria-label={isPaused ? 'Play slider' : 'Pause slider'}
      >
        {isPaused ? '▶' : 'Ⅱ'}
      </button>

      {/* ================= MAIN CONTENT ================= */}

      <div
        style={styles.contentContainer}
        className="hero-content-container"
      >
        <div
          key={currentSlide}
          style={styles.content}
          className="hero-content"
        >
          <div
            style={styles.eyebrow}
            className="hero-eyebrow"
          >
            <span style={styles.eyebrowDot} />

            {activeSlide.eyebrow}
          </div>

          <h1
            style={styles.heading}
            className="hero-heading"
          >
            {activeSlide.title}

            <span style={styles.highlight}>
              {activeSlide.highlight}
            </span>
          </h1>

          <p
            style={styles.description}
            className="hero-description"
          >
            {activeSlide.description}
          </p>

          <div
            style={styles.buttonRow}
            className="hero-buttons"
          >
            <a
              href="#services"
              style={styles.primaryButton}
              className="hero-primary-btn"
            >
              {activeSlide.primary}

              <span>→</span>
            </a>

            <a
              href="#contact"
              style={styles.secondaryButton}
              className="hero-secondary-btn"
            >
              {activeSlide.secondary}

              <span>↗</span>
            </a>
          </div>
        </div>
      </div>

      {/* ================= SIDE LABEL ================= */}

      <div
        style={styles.sideLabel}
        className="hero-side-label"
      >
        SANSA CONSULTANT INDIA LLP
      </div>

      {/* ================= INDICATORS ================= */}

      <div
        style={styles.indicators}
        className="hero-indicators"
      >
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() =>
              goToSlide(
                index,
                index > currentSlide ? 'next' : 'prev'
              )
            }
            style={styles.indicator(index === currentSlide)}
          />
        ))}
      </div>

      {/* ================= ARROWS ================= */}

      <div
        style={styles.sliderControls}
        className="hero-controls"
      >
        <button
          type="button"
          onClick={previousSlide}
          style={styles.arrowButton}
          className="hero-arrow"
          aria-label="Previous slide"
        >
          ←
        </button>

        <button
          type="button"
          onClick={nextSlide}
          style={styles.arrowButton}
          className="hero-arrow"
          aria-label="Next slide"
        >
          →
        </button>
      </div>

      {/* ================= SCROLL INDICATOR ================= */}

      <div
        style={styles.scrollIndicator}
        className="hero-scroll"
      >
        <span>Scroll to explore</span>

        <div
          style={styles.scrollLine}
          className="hero-scroll-line"
        />
      </div>
    </section>
  );
};

export default Landingscreen;